import { useEffect, useRef, useState } from 'react'
import { Plus, Trash2, Loader2, Package, ArrowLeft, Check, ExternalLink, Mail } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { cn } from '../../lib/shadcn/utils'
import { toast } from '../../lib/shadcn/sonner'
import { fulfillmentSyncWarning } from '../../utils/syncResults'
import {
  useGetShippingRates,
  usePurchaseShippingLabels,
  useCloseOrderAsShipped,
} from '../../hooks/backend/scanPage'
import {
  useShippingSettings
} from '../../utils/shippingSettings'

interface PackageRow {
  id: string
  presetId: string
  length: string
  width: string
  height: string
  weight: string
}

interface RateOption {
  key: string
  provider: string
  serviceLevel: string
  estimatedDays: number | null
  totalAmount: string
  currency: string
  shippingProvider: 'shippo' | 'envia'
  purchaseData:
    | { type: 'shippo'; packageRates: Array<{ packageIndex: number; rateId: string; amount: string }> }
    | { type: 'envia'; carrier: string; service: string; amount: string }
}

interface PurchasedLabel {
  packageIndex: number
  trackingNumber: string | null
  labelUrl: string
}

function makeRowId(): string {
  return `pkg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

const makePackageRow = (
  presetId: string,
  presets: Array<{
    id: string
    length: number
    width: number
    height: number
  }>
): PackageRow => {
  const preset = presets.find(
    p => p.id === presetId
  )

  return {
    id: makeRowId(),
    presetId,
    length: preset ? String(preset.length) : '',
    width: preset ? String(preset.width) : '',
    height: preset ? String(preset.height) : '',
    weight: '',
  }
}

export default function ShipOrderDialog({
  orderId,
  onOpenChange,
  onShipped,
  onEmailTracking,
}: {
  orderId: string | null
  onOpenChange: (open: boolean) => void
  onShipped: () => void
  onEmailTracking?: (orderId: string) => void
}) {
  const {
    settings,
    loaded: shippingSettingsLoaded,
    loading: shippingSettingsLoading,
  } = useShippingSettings()
  
  const {
    shippingProvider,
    enviaCarriers,
    shipFromAddress,
    parcelPresets: presets,
    defaultParcelPresetId: defaultPresetId,
  } = settings
  const getShippingRates = useGetShippingRates()
  const purchaseShippingLabels = usePurchaseShippingLabels()
  const closeOrderAsShipped = useCloseOrderAsShipped()

  const bodyRef = useRef<HTMLDivElement>(null)
  const [packages, setPackages] = useState<PackageRow[]>([])
  const [step, setStep] = useState<'packages' | 'rates' | 'done'>('packages')
  const [rateOptions, setRateOptions] = useState<RateOption[]>([])
  const [selectedRateKey, setSelectedRateKey] = useState<string | null>(null)
  const [purchasedLabels, setPurchasedLabels] = useState<PurchasedLabel[]>([])
  // Purchase errors/warnings stay in the dialog so they can be read in full.
  const [purchaseError, setPurchaseError] = useState<string | null>(null)
  const [purchaseWarning, setPurchaseWarning] = useState<string | null>(null)

  const open = orderId !== null

  useEffect(() => {
  if (open && shippingSettingsLoaded) {
    setPackages([
      makePackageRow(
        defaultPresetId,
        presets
      ),
    ])

    setStep('packages')
    setRateOptions([])
    setSelectedRateKey(null)
    setPurchasedLabels([])
    setPurchaseError(null)
    setPurchaseWarning(null)
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  open,
  orderId,
  shippingSettingsLoaded,
  defaultPresetId,
])

  const addPackage = () => {
  setPackages(prev => [
    ...prev,
    makePackageRow(
      defaultPresetId,
      presets
    ),
  ])
  // Bring the new package into view.
  requestAnimationFrame(() => {
    const el = bodyRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  })
}

  const removePackage = (id: string) => {
    setPackages(prev => (prev.length > 1 ? prev.filter(p => p.id !== id) : prev))
  }

  const updatePackage = (id: string, patch: Partial<PackageRow>) => {
    setPackages(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)))
  }

  const handlePresetChange = (
  packageId: string,
  presetId: string
) => {
  const preset = presets.find(
    p => p.id === presetId
  )

  updatePackage(packageId, {
    presetId,
    length: preset ? String(preset.length) : '',
    width: preset ? String(preset.width) : '',
    height: preset ? String(preset.height) : '',
  })
}

  const buildPackagePayload = () =>
  packages.map(p => ({
    length: Number(p.length),
    width: Number(p.width),
    height: Number(p.height),
    weight: Number(p.weight),
  }))

  const handleGetRates = async () => {
  if (!orderId) return

  if (!shippingSettingsLoaded) {
    toast.error('Shipping settings are still loading')
    return
  }
    if (packages.some(p => !p.presetId)) {
      toast.error('Choose a parcel size for every package')
      return
    }
    if (packages.some(p => !Number(p.weight) || Number(p.weight) <= 0)) {
      toast.error('Enter a positive weight for every package')
      return
    }
    if (
  packages.some(
    p =>
      !Number(p.length) ||
      Number(p.length) <= 0 ||
      !Number(p.width) ||
      Number(p.width) <= 0 ||
      !Number(p.height) ||
      Number(p.height) <= 0
  )
) {
  toast.error(
    'Enter positive dimensions for every package'
  )
  return
}
    try {
      const result = (await getShippingRates.trigger({
        order_id: orderId,
        shipping_provider: shippingProvider,
        envia_carriers: shippingProvider === 'envia' ? enviaCarriers : undefined,
        ship_from: shipFromAddress,
        packages: buildPackagePayload(),
      })) as { options: RateOption[] }
      setRateOptions(result.options ?? [])
      setSelectedRateKey(result.options?.[0]?.key ?? null)
      setStep('rates')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to fetch shipping rates')
    }
  }

  const handlePurchase = async () => {
    if (!orderId) return
    const option = rateOptions.find(o => o.key === selectedRateKey)
    if (!option) {
      toast.error('Select a shipping option')
      return
    }
    setPurchaseError(null)
    setPurchaseWarning(null)
    try {
      const result = (await purchaseShippingLabels.trigger({
        order_id: orderId,
        shipping_provider: option.shippingProvider,
        carrier: option.provider,
        service_level: option.serviceLevel,
        purchase_data: option.purchaseData,
        ship_from: shipFromAddress,
        packages: buildPackagePayload(),
      })) as { labels: PurchasedLabel[]; warning?: string | null }
      setPurchasedLabels(
  result.labels ?? []
)

setPurchaseWarning(result.warning ?? null)
setStep('done')

toast.success(
  `${result.labels?.length ?? 0} label(s) generated`
)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to generate shipping label(s)'
      setPurchaseError(message)
      toast.error(
        message.length > 200 ? `${message.slice(0, 200)}… (full error in the dialog)` : message,
        { duration: 20000, closeButton: true },
      )
    }
  }

  const handleCloseOrder = async () => {
  if (!orderId) return

  try {
    const result = await closeOrderAsShipped.trigger({
      order_id: orderId,
    })

    toast.success(
      'Order marked as shipped'
    )
    const warning = fulfillmentSyncWarning(
      (result as { sync_results?: unknown } | undefined)?.sync_results
    )
    if (warning) toast.warning(warning)

    onShipped()
    onOpenChange(false)
  } catch (e) {
    toast.error(
      e instanceof Error
        ? e.message
        : 'Failed to close order'
    )
  }
}

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onOpenChange(false) }}>
      <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Ship order {orderId}</DialogTitle>
          <DialogDescription>
            {step === 'packages' &&
              'Add one package per box you are shipping, choose its size, and enter its weight.'}
            {step === 'rates' && 'Choose a shipping option. The price shown covers all packages.'}
            {step === 'done' && 'Labels generated. Are you finished shipping this order?'}
          </DialogDescription>
        </DialogHeader>

        {/* Only this middle part scrolls, so orders with many packages stay
            reachable and the title and buttons stay in view. */}
        <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto space-y-4">
        {step === 'packages' && (
          <div className="space-y-3">
            {presets.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No parcel presets configured. Add one in Settings before shipping.
              </p>
            ) : (
              <>
                {packages.map((pkg, i) => (
                  <div
  key={pkg.id}
  className="border rounded-md p-3 space-y-3"
>
  <div className="flex items-end gap-2">
    <div className="flex-1 space-y-1.5">
      <Label>Package {i + 1} size</Label>

      <Select
        value={pkg.presetId}
        onValueChange={v =>
          handlePresetChange(pkg.id, v)
        }
      >
        <SelectTrigger>
          <SelectValue placeholder="Select a size" />
        </SelectTrigger>

        <SelectContent>
          {presets.map(preset => (
            <SelectItem
              key={preset.id}
              value={preset.id}
            >
              {preset.name} ({preset.length} x{' '}
              {preset.width} x {preset.height} in)
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="text-muted-foreground hover:text-destructive"
      aria-label={`Remove package ${i + 1}`}
      disabled={packages.length === 1}
      onClick={() =>
        removePackage(pkg.id)
      }
    >
      <Trash2 className="w-4 h-4" />
    </Button>
  </div>

  <div className="grid grid-cols-4 gap-2">
    <div className="space-y-1.5">
      <Label>Length (in)</Label>
      <Input
        type="number"
        min={0}
        step="0.1"
        value={pkg.length}
        onChange={e =>
          updatePackage(pkg.id, {
            length: e.target.value,
          })
        }
      />
    </div>

    <div className="space-y-1.5">
      <Label>Width (in)</Label>
      <Input
        type="number"
        min={0}
        step="0.1"
        value={pkg.width}
        onChange={e =>
          updatePackage(pkg.id, {
            width: e.target.value,
          })
        }
      />
    </div>

    <div className="space-y-1.5">
      <Label>Height (in)</Label>
      <Input
        type="number"
        min={0}
        step="0.1"
        value={pkg.height}
        onChange={e =>
          updatePackage(pkg.id, {
            height: e.target.value,
          })
        }
      />
    </div>

    <div className="space-y-1.5">
      <Label>Weight (lb)</Label>
      <Input
        type="number"
        min={0}
        step="0.1"
        value={pkg.weight}
        onChange={e =>
          updatePackage(pkg.id, {
            weight: e.target.value,
          })
        }
        placeholder="0.0"
      />
    </div>
  </div>
</div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addPackage} className="gap-1.5">
                  <Plus className="w-4 h-4" />
                  Add Package
                </Button>
              </>
            )}
          </div>
        )}

        {step === 'rates' && (
          <div className="space-y-2">
            {rateOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No shipping options available.</p>
            ) : (
              rateOptions.map(option => {
                const selected = selectedRateKey === option.key
                return (
                  <button
                    type="button"
                    key={option.key}
                    onClick={() => setSelectedRateKey(option.key)}
                    className={cn(
                      'w-full flex items-center justify-between gap-4 rounded-lg border p-3 text-left transition-colors',
                      selected ? 'border-primary bg-accent' : 'border-border hover:bg-accent/50'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-muted-foreground shrink-0" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {option.provider} {option.serviceLevel}
                          </span>
                          {selected && <Check className="w-4 h-4 text-primary" />}
                        </div>
                        {option.estimatedDays !== null && (
                          <p className="text-xs text-muted-foreground">
                            Est. {option.estimatedDays} business day{option.estimatedDays === 1 ? '' : 's'}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="font-semibold text-sm shrink-0">
                      ${option.totalAmount} {option.currency}
                    </span>
                  </button>
                )
              })
            )}
          </div>
        )}

        {step === 'rates' && purchaseError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
            <p className="font-medium text-destructive">Label not saved</p>
            <p className="text-xs text-muted-foreground mb-2">
              Check Envia before trying again so you aren&apos;t charged twice.
            </p>
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all select-text text-xs">
              {purchaseError}
            </pre>
          </div>
        )}

        {step === 'done' && purchaseWarning && (
          <p className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
            {purchaseWarning}
          </p>
        )}

        {step === 'done' && (
          <div className="space-y-2">
            {purchasedLabels.map(label => (
              <a
                key={label.packageIndex}
                href={label.labelUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-2 rounded-md border p-3 text-sm hover:bg-accent/50 transition-colors"
              >
                <span>
                  Package {label.packageIndex + 1}
                  {label.trackingNumber ? ` — ${label.trackingNumber}` : ''}
                </span>
                <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
              </a>
            ))}
          </div>
        )}
        </div>

        <DialogFooter>
          {step === 'packages' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
  onClick={() => void handleGetRates()}
  disabled={
    shippingSettingsLoading ||
    !shippingSettingsLoaded ||
    getShippingRates.loading ||
    presets.length === 0
  }
>
  {shippingSettingsLoading || getShippingRates.loading ? (
    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
  ) : null}

  {shippingSettingsLoading
    ? 'Loading Settings...'
    : 'Get Rates'}
</Button>
            </>
          )}
          {step === 'rates' && (
            <>
              <Button variant="outline" onClick={() => setStep('packages')} className="gap-1.5">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Button
                onClick={() => void handlePurchase()}
                disabled={purchaseShippingLabels.loading || !selectedRateKey}
              >
                {purchaseShippingLabels.loading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Package className="w-4 h-4 mr-1" />
                )}
                Generate Label{packages.length > 1 ? 's' : ''}
              </Button>
            </>
          )}
          {step === 'done' && (
  <>
    {onEmailTracking && orderId && (
      <Button
  variant="outline"
  onClick={() => {
  onEmailTracking?.(orderId)
}}
  className="gap-1.5"
>
  <Mail className="w-4 h-4" />
  Email Tracking
</Button>
    )}

    <Button
      variant="outline"
      onClick={() => {
        onOpenChange(false)
      }}
    >
      Keep Order Open
    </Button>

    <Button
      onClick={() =>
        void handleCloseOrder()
      }
      disabled={
        closeOrderAsShipped.loading
      }
    >
      {closeOrderAsShipped.loading ? (
        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
      ) : (
        <Check className="w-4 h-4 mr-1" />
      )}

      Close Order
    </Button>
  </>
)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
